/* Content — compact layout (option 1b). Library rail (Life, Teasing, Paid
   content) with each library's Enabled toggle; Paid content has Vaults /
   Pricing / Sales sub-tabs, Life and Teasing are single pages. Every field
   saves on change. */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account, ContentPolicy, ContentPolicyBody, LifeContentSettings, LifeContentSettingsBody, PpvOfferPolicyBody, TeasingContentSettingsBody } from '../../../../api/client';
import { defaultPricingRules, hasAnyPricing, readStandardBundles } from '../../ContentRequests/pricing';
import { AdvancedToggle, Card, ErrorLine, Loading, NumInput, PageFrame, Row, SaveStatus, Section, SectionRail, Seg, Stack, SubTabs, TextArea, Toggle, type RailRow } from '../kit';
import { mergeStatus } from '../style';
import { usePolicy, patchNow } from '../usePolicy';
import { PPV_DEFAULTS, ppvBodyOf } from '../monetisation/model';
import { VaultsSection } from './VaultsSection';
import { BundlesSection } from './BundlesSection';
import { PricingTab } from './PricingTab';
import { SalesTab } from './SalesTab';

type Lib = 'life' | 'teasing' | 'paid';
type View = 'vaults' | 'pricing' | 'sales';

const POLICY_DEFAULT: ContentPolicyBody = {
  is_active: true, automate_mode: 'handoff', pricing_rules: defaultPricingRules(), no_content_handoff: true,
  max_negotiation_attempts: 3, on_max_negotiations: 'ease_to_communication', strategy_prompt: null,
};
const policyBodyOf = (p: ContentPolicy): ContentPolicyBody => ({
  is_active: p.is_active, automate_mode: p.automate_mode,
  pricing_rules: hasAnyPricing(p.pricing_rules) ? p.pricing_rules : defaultPricingRules(),
  no_content_handoff: p.no_content_handoff, max_negotiation_attempts: p.max_negotiation_attempts,
  on_max_negotiations: p.on_max_negotiations, strategy_prompt: p.strategy_prompt,
});
const lifeBodyOf = (s: LifeContentSettings): LifeContentSettingsBody => ({
  is_life_content_allowed: s.is_life_content_allowed, max_sends_per_session: s.max_sends_per_session, aggression: s.aggression, detection_prompt: s.detection_prompt ?? null,
});

export function ContentPage({ account }: { account: Account }) {
  const { t } = useTranslation('assistantCompact');
  const id = account.account_id;
  const [params, setParams] = useSearchParams();
  const lib: Lib = (['life', 'teasing', 'paid'] as Lib[]).includes(params.get('lib') as Lib) ? (params.get('lib') as Lib) : 'paid';
  const view: View = (['vaults', 'pricing', 'sales'] as View[]).includes(params.get('view') as View) ? (params.get('view') as View) : 'vaults';
  const setParam = (k: string, v: string) => { const n = new URLSearchParams(params); n.set(k, v); setParams(n, { replace: true }); };
  const [lifeAdv, setLifeAdv] = useState(false);
  const [paidVaults, setPaidVaults] = useState<number | null>(null);

  const life = usePolicy<LifeContentSettingsBody>(
    () => api.getLifeContentSettings(id).then(lifeBodyOf),
    (b) => api.putLifeContentSettings(id, b).then(lifeBodyOf),
    { is_life_content_allowed: false, max_sends_per_session: 1, aggression: 'balanced', detection_prompt: null }, id,
  );
  const teasing = usePolicy<TeasingContentSettingsBody>(
    () => api.getTeasingContentSettings(id).then((s) => ({ is_teasing_content_allowed: s.is_teasing_content_allowed })),
    (b) => api.putTeasingContentSettings(id, b).then((s) => ({ is_teasing_content_allowed: s.is_teasing_content_allowed })),
    { is_teasing_content_allowed: false }, id,
  );
  const policy = usePolicy<ContentPolicyBody>(() => api.getContentPolicy(id).then(policyBodyOf), (b) => api.putContentPolicy(id, b).then(policyBodyOf), POLICY_DEFAULT, id);
  // The pricing lives on the content-request policy; PPV offers always quote it.
  const ppv = usePolicy<PpvOfferPolicyBody>(
    () => api.getPpvOfferPolicy(id).then(ppvBodyOf),
    (b) => api.putPpvOfferPolicy(id, { ...b, use_content_request_pricing: true }).then(ppvBodyOf),
    PPV_DEFAULTS, id,
  );

  const rail: RailRow[] = [
    { id: 'life', label: t('lib.life'), on: life.state === 'ready' ? life.body.is_life_content_allowed : null, onToggle: (v) => patchNow(life, { is_life_content_allowed: v }) },
    { id: 'teasing', label: t('lib.teasing'), on: teasing.state === 'ready' ? teasing.body.is_teasing_content_allowed : null, onToggle: (v) => patchNow(teasing, { is_teasing_content_allowed: v }) },
    {
      id: 'paid', label: t('lib.paid'), on: policy.state === 'ready' ? policy.body.automate_mode === 'automate' : null,
      onToggle: (v) => patchNow(policy, { automate_mode: v ? 'automate' : 'handoff' }), toggleTitle: t('lib.paidToggle'),
    },
  ];
  const status = lib === 'life' ? life.auto : lib === 'teasing' ? teasing.auto : mergeStatus(policy.auto, ppv.auto);
  const L = life.body;

  return (
    <PageFrame
      rail={<SectionRail heading={t('lib.rail')} items={rail} active={lib} onSelect={(v) => setParam('lib', v)} />}
      headline={t(`lib.${lib}`)}
      desc={t(`lib.${lib}Desc`)}
      right={<SaveStatus status={status.status} error={status.error} onRetry={status.retry} idleLabel />}
      tabs={lib === 'paid' ? <SubTabs value={view} onChange={(v) => setParam('view', v)} options={[{ id: 'vaults', label: t('lib.vaults') }, { id: 'pricing', label: t('lib.pricing') }, { id: 'sales', label: t('lib.sales') }]} /> : undefined}
    >
      {lib === 'life' && (
        <Stack>
          <VaultsSection key="life" accountId={id} section="life" />
          <Section title={t('life.title')} hint={t('life.hint')}>
            {life.state === 'loading' ? <Loading /> : life.state === 'failed' ? <ErrorLine>{t('loadFailed')}</ErrorLine> : (
              <>
                <Card>
                  <Row label={t('life.share')} help={L.is_life_content_allowed ? t('life.shareOn') : t('life.shareOff')}>
                    <Toggle on={L.is_life_content_allowed} onChange={(v) => patchNow(life, { is_life_content_allowed: v })} />
                  </Row>
                  {L.is_life_content_allowed && (
                    <>
                      <Row label={t('life.eager')} help={t(`life.eagerHelp.${L.aggression}`)}>
                        <Seg value={L.aggression} onChange={(v) => life.patch({ aggression: v })} options={[{ id: 'reserved', label: t('life.reserved') }, { id: 'balanced', label: t('life.balanced') }, { id: 'eager', label: t('life.eagerOpt') }]} />
                      </Row>
                      <Row label={t('life.max')} help={t('life.maxHelp')}>
                        <NumInput value={L.max_sends_per_session} onChange={(v) => v !== null && life.patch({ max_sends_per_session: v })} min={1} max={10} int width={64} />
                      </Row>
                    </>
                  )}
                </Card>
                {L.is_life_content_allowed && (
                  <>
                    <AdvancedToggle open={lifeAdv} onToggle={() => setLifeAdv((a) => !a)} hint={t('life.advHint')} />
                    {lifeAdv && (
                      <Card>
                        <Row stack label={t('life.guidance')} help={t('life.guidanceHint')}>
                          <TextArea value={L.detection_prompt ?? ''} onChange={(v) => life.patch({ detection_prompt: v || null })} placeholder={t('life.guidancePh')} height={64} />
                        </Row>
                      </Card>
                    )}
                  </>
                )}
              </>
            )}
          </Section>
        </Stack>
      )}
      {lib === 'teasing' && <VaultsSection key="teasing" accountId={id} section="teasing" />}
      {lib === 'paid' && (
        policy.state === 'loading' ? <Loading /> : policy.state === 'failed' ? <ErrorLine>{t('loadFailed')}</ErrorLine> : (
          <>
            {view === 'vaults' && (
              <Stack>
                <VaultsSection key="paid" accountId={id} section="paid" info={t('lib.paidInfo')} onVaults={setPaidVaults} />
                <BundlesSection accountId={id} shapes={readStandardBundles(policy.body.pricing_rules)} />
              </Stack>
            )}
            {view === 'pricing' && <PricingTab rules={policy.body.pricing_rules} onRules={(r) => policy.patch({ pricing_rules: r })} noVaults={paidVaults === 0} />}
            {view === 'sales' && <SalesTab policy={policy.body} setPolicy={policy.patch} ppv={ppv.body} setPpv={ppv.patch} ppvReady={ppv.state === 'ready'} />}
          </>
        )
      )}
    </PageFrame>
  );
}
